import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";
import { useUiLang, useUiLangStore } from "../../../stores/uiLang";
import { peT } from "../../../i18n/peLesson";
import LessonSequence from "./LessonSequence";
import LessonErrorBoundary from "./shared/LessonErrorBoundary";
import MissingChapter from "./shared/MissingChapter";
import type { Lang, Tier } from "../../../api/serving";
import { isStudentAssetType, type StudentAssetType } from "../../../engine/sequenceEngine";

const TIERS: Tier[] = ["T1", "T2", "T3", "T4"];

interface ResolvedLesson {
  chapterId: string;
  tier: Tier;
  lang: Lang;
  /** student_assignments row id — the /pe/submit key for the summative quiz.
   *  Added after parseLessonKey, so optional here. */
  studentAssignmentId?: string | null;
  /**
   * Student assets to ship, from assignments.deployment (spec §5 picker).
   * Undefined ⇒ no/legacy deployment ⇒ the hook builds the full CANONICAL_STEPS
   * loop (today's proven default, zero-regression).
   */
  assets?: StudentAssetType[];
}

/**
 * Native (non-LTI) wrapper around the Scene Reader.
 *
 * Where `/lti/scenes` gets chapter_id/tier/lang from LTI launch query params
 * (see useLaunchParams), this route resolves them from the student's OWN
 * assignment row. The teacher assigns a PE chapter via CreatePeAssignmentModal,
 * which stores the content key as a composite `assessment_id`:
 *
 *     pe:{chapterId}:{tier}:{lang}      e.g. "pe:8:T1:en"
 *
 * We look up the student_assignments row (RLS scopes it to the logged-in
 * student) joined to its assignment, confirm it's a `pe_chapter` type, parse the
 * composite key, then hand the same props to the shared SceneReader. The reader
 * itself is untouched — it has never known about LTI.
 */
const StudentLessonPage: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const user = useUserStore((state) => state.user);
  const authResolved = useUserStore((state) => state.authResolved);

  const [lesson, setLesson] = useState<ResolvedLesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const setUiLang = useUiLangStore((s) => s.setLang);
  const uiLang = useUiLang();

  // Run E2 J2: the site header and footer follow the assignment language.
  useEffect(() => {
    if (lesson?.lang === "en" || lesson?.lang === "es") setUiLang(lesson.lang);
  }, [lesson?.lang, setUiLang]);

  useEffect(() => {
    // Wait for auth before deciding anything — a cold load has no user yet.
    if (!authResolved) return;
    if (!user?.id || !assignmentId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    (async () => {
      // Try selecting the §5 `deployment` column; if it isn't present yet (the
      // DB-writer terminal owns the column add), the embed errors — fall back to
      // the proven minimal select so the route NEVER regresses. Either way the
      // student gets a working lesson; only the asset NARROWING is deferred.
      const BASE = "id, assignment_id, student_id, assignments ( type, assessment_id";
      let { data, error } = await supabase
        .from("student_assignments")
        .select(`${BASE}, deployment )`)
        .eq("assignment_id", assignmentId)
        .eq("student_id", user.id)
        .maybeSingle();

      if (error) {
        // deployment column may not exist yet — retry without it (full default).
        ({ data, error } = await supabase
          .from("student_assignments")
          .select(`${BASE} )`)
          .eq("assignment_id", assignmentId)
          .eq("student_id", user.id)
          .maybeSingle());
      }

      if (cancelled) return;

      const assignment = (data as any)?.assignments;
      const resolved = parseLessonKey(assignment?.type, assignment?.assessment_id);

      if (error || !resolved) {
        setNotFound(true);
        setLesson(null);
      } else {
        // Null/legacy/malformed deployment ⇒ undefined ⇒ full CANONICAL_STEPS.
        setLesson({
          ...resolved,
          studentAssignmentId: (data as any)?.id ?? null,
          assets: parseDeployment(assignment?.deployment),
        });
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [authResolved, user?.id, assignmentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">{peT(uiLang).loadingLesson}</p>
        </div>
      </div>
    );
  }

  if (notFound || !lesson) {
    return <MissingChapter screen="reader" />;
  }

  // The engine drives the locked sequence; SceneReader is one step inside it.
  // assignmentId (route param) === assignments.id (uuid); user.id === student.
  return (
    <LessonErrorBoundary>
      <LessonSequence
        chapterId={lesson.chapterId}
        tier={lesson.tier}
        lang={lesson.lang}
        assignmentId={assignmentId ?? null}
        studentId={user?.id ?? null}
        studentAssignmentId={lesson.studentAssignmentId ?? null}
        assets={lesson.assets}
        dashboardPath="/dashboard"
      />
    </LessonErrorBoundary>
  );
};

/**
 * Parse assignments.deployment (jsonb `{ presetId, assets }`) into a validated
 * StudentAssetType[]. Returns undefined for null/legacy/malformed/empty input so
 * the caller falls back to the full CANONICAL_STEPS loop (zero-regression). Each
 * asset is checked against isStudentAssetType — the same type boundary the
 * engine enforces, so a teacher-only value can never slip through here either.
 */
export function parseDeployment(deployment: unknown): StudentAssetType[] | undefined {
  if (!deployment || typeof deployment !== "object") return undefined;
  const raw = (deployment as { assets?: unknown }).assets;
  if (!Array.isArray(raw)) return undefined;
  const assets = raw.filter(
    (a): a is StudentAssetType => typeof a === "string" && isStudentAssetType(a),
  );
  return assets.length > 0 ? assets : undefined;
}

/** Parse the composite `assessment_id` for a `pe_chapter` assignment. */
function parseLessonKey(
  type: string | undefined,
  assessmentId: string | undefined
): ResolvedLesson | null {
  if (type !== "pe_chapter" || !assessmentId) return null;
  const parts = assessmentId.split(":");
  if (parts.length !== 4 || parts[0] !== "pe") return null;

  const [, chapterId, tierRaw, langRaw] = parts;
  const tier = tierRaw.toUpperCase() as Tier;
  const lang = langRaw.toLowerCase() as Lang;

  if (!chapterId || !TIERS.includes(tier)) return null;
  if (lang !== "en" && lang !== "es") return null;

  return { chapterId, tier, lang };
}

export default StudentLessonPage;
